import React from 'react'
import {BookOpen, User, Landmark} from 'lucide-react'

export default function HistoryPage() {
  const articles = [
    {
      icon: User,
      title: "Who Was Colonel Albert B. Blanton?",
      subtitle: "The Father of Single Barrel Bourbon",
      content: "Colonel Albert Bacon Blanton (1881-1959) was a legendary figure in bourbon history. Starting as an office boy at the O.F.C. Distillery (now Buffalo Trace) at age 16, he rose to become president and spent 55 years perfecting his craft. Col Blanton pioneered the practice of hand-selecting exceptional barrels for special occasions—a tradition that eventually led to the creation of Blanton's Single Barrel Bourbon in 1984, named in his honor.",
      keywords: "Albert B Blanton, Col Blanton"
    },
    {
      icon: Landmark,
      title: "The Single Oak Project Explained",
      subtitle: "Buffalo Trace's Groundbreaking Experiment",
      content: "The Single Oak Project was an ambitious 14-year experiment by Buffalo Trace Distillery that created 1,396 unique bourbon expressions from 96 individually monitored trees. Each variable—tree, grain recipe, warehouse location, entry proof, and stave seasoning—was isolated to understand their impact on flavor. Released between 2011-2015, it remains the most comprehensive bourbon maturation study ever conducted, revealing how dramatically single barrel characteristics can vary.",
      keywords: "Single oak project"
    },
    {
      icon: Landmark,
      title: "Wathen's Bourbon Distillery History",
      subtitle: "Kentucky's Oldest Bourbon Family",
      content: "The Wathen family represents Kentucky's oldest bourbon dynasty, with roots tracing back to 1788 when Henry Hudson Wathen first distilled whiskey. The Wathen's Bourbon Distillery in Owensboro became legendary for its wheated bourbon recipes. Though the original distillery closed, the family recipes live on through partnerships with modern distilleries. Wathen's Single Barrel expressions continue this 200+ year legacy of craftsmanship.",
      keywords: "Wathen's bourbon distillery"
    }
  ]

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#3E2723] mb-6">
            Bourbon History & Education
          </h1>
          <p className="text-xl text-[#3E2723]/70 max-w-3xl mx-auto leading-relaxed">
            Deep dives into the legends, distilleries, and innovations that shaped single barrel bourbon culture.
          </p>
        </div>

        {/* Articles */}
        <div className="space-y-12">
          {articles.map((article, idx) => (
            <article key={idx} className="bg-white rounded-2xl shadow-lg border border-[#B87333]/20 overflow-hidden hover:shadow-2xl transition-all">
              <div className="grid grid-cols-1 lg:grid-cols-3">
                {/* Icon Section */}
                <div className="bg-gradient-to-br from-[#3E2723] to-[#4E342E] p-12 flex items-center justify-center">
                  <article.icon className="w-32 h-32 text-[#F59E0B]" />
                </div>

                {/* Content Section */}
                <div className="lg:col-span-2 p-8 lg:p-12">
                  <div className="flex items-center space-x-3 mb-4">
                    <BookOpen className="w-6 h-6 text-[#D97706]" />
                    <span className="text-sm text-[#D97706] font-medium uppercase tracking-wide">Historical Deep Dive</span>
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-3">
                    {article.title}
                  </h2>
                  
                  <p className="text-[#D97706] font-medium text-lg mb-6">
                    {article.subtitle}
                  </p>
                  
                  <p className="text-[#3E2723]/80 text-lg leading-relaxed mb-6">
                    {article.content}
                  </p>

                  <div className="bg-[#FAF8F5] border border-[#B87333]/20 rounded-lg p-4">
                    <p className="text-xs text-[#3E2723]/60 mb-1">Target Keywords:</p>
                    <p className="text-sm text-[#3E2723]/80 italic">{article.keywords}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Additional Context Section */}
        <div className="mt-16 bg-gradient-to-br from-[#3E2723] to-[#4E342E] rounded-2xl p-12 border border-[#B87333]/30">
          <h2 className="text-3xl font-bold text-[#FAF8F5] mb-8 text-center">
            Why Single Barrel History Matters
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-[#FAF8F5]/80 text-lg leading-relaxed">
            <div>
              <p className="mb-4">
                Understanding the pioneers and experiments behind single barrel bourbon deepens appreciation for every pour. These aren't just spirits—they're liquid time capsules carrying decades of craft tradition.
              </p>
            </div>
            <div>
              <p>
                From Colonel Blanton's hand-selected barrels to Buffalo Trace's scientific rigor with the Single Oak Project, each innovation shaped how we experience bourbon today.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
